import { Alert, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useEffect, useMemo, useState } from 'react';
import { InlineLoader } from '../../common/components/InlineLoader';
import { getErrorMessage } from '../../common/errors/errors';
import { useFormItem } from '../../common/hooks/use-form-item/index';
import { InputType, ValidatorFunction } from '../../common/hooks/use-form-item/types';
import { and } from '../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../common/hooks/use-form-item/validators/existential';
import { greaterOrEqualThan, lessOrEqualThan, maxLength } from '../../common/hooks/use-form-item/validators/operational';
import { Profile } from '../../common/models/Profile';
import { OrganizationService } from '../services/OrganizationService';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';

type Props = {
  prefill?: {
    name: string;
    description: string | null;
    profileId: number | null;
    maxItemCount: number;
  };
  submit: {
    label: string;
    action: (name: string, description: string, profileId: number, maxItemCount: number) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
  unlimitedReports?: boolean;
};

const DEFAULT_PROJECT_MAX_ITEM_COUNT = 20;
const MIN_PROJECT_MAX_ITEM_COUNT = 2;
const MAX_PROJECT_MAX_ITEM_COUNT = 100;

const ProfileOption = (props: { profile: Profile }) => {
  const overflowSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <Stack rowGap={0.5} overflow="hidden">
      <Typography variant="body1" sx={overflowSx}>
        {props.profile.getName()}
      </Typography>
      <Typography variant="overline" sx={{ ...overflowSx, color: 'grey.600', lineHeight: 1, pb: 0.5 }}>
        {props.profile.getTypeLabel()}
      </Typography>
    </Stack>
  );
};

export const ProjectForm = ({ prefill, submit, back, unlimitedReports = false }: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);

  // Hide the profile input when the form is being used for editing the project within modal
  // Profile field will be displayed but disabled on project settings page
  const showProfileInput = !prefill;
  const disableInputs = loading;

  useEffect(() => {
    const organizationService = new OrganizationService();

    const fetchProfiles = async () => {
      try {
        const profiles = await organizationService.getProfiles();
        setProfiles(profiles);
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };

    // The profile select is not displayed in editing mode: avoid the profile query
    if (showProfileInput) {
      fetchProfiles();
    }

    // Cancel all requests on unmount
    return () => organizationService.cancelAllRequests();
  }, [showProfileInput]);

  const [name, nameComponent, isNameValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('projectForm.name'),
    value: prefill?.name || '',
    validator: and(required(), maxLength(1000)),
    inputOptions: {
      autoFocus: true,
      disabled: disableInputs,
      showRequiredIndicator: true,
    },
    textOptions: { trimOnBlur: true },
  });

  const [description, descriptionComponent, isDescriptionValid] = useFormItem<string>({
    type: InputType.TYPE_TEXT,
    label: t('projectForm.description'),
    value: prefill?.description || '',
    validator: maxLength(1000),
    inputOptions: {
      disabled: disableInputs,
    },
    textOptions: { trimOnBlur: true },
  });

  const [profileId, profileComponent, isProfileValid] = useFormItem<number | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t('projectForm.profile.input'),
    value: prefill?.profileId || null,
    validator: showProfileInput ? required() : null,
    inputOptions: {
      disabled: !showProfileInput || disableInputs,
      focusedLabel: t('projectForm.profile.focused'),
      showRequiredIndicator: true,
    },
    autocompleteOptions: {
      readOnly: true,
      clearable: false,
      options: profiles?.map((p) => ({
        value: p.getId(),
        label: p.getLabel(),
        element: <ProfileOption profile={p} />,
        nativeOptionHover: true,
      })),
    },
  });

  // In the case of unlimitedReports, we have slightly different validation
  const maxItemCountValidator = useMemo(() => {
    const validatorFunctions: [ValidatorFunction, ...ValidatorFunction[]] = [required(), greaterOrEqualThan(MIN_PROJECT_MAX_ITEM_COUNT)];

    if (!unlimitedReports) {
      validatorFunctions.push(lessOrEqualThan(MAX_PROJECT_MAX_ITEM_COUNT));
    }

    return and(...validatorFunctions);
  }, [unlimitedReports]);

  const [maxItemCount, maxItemCountComponent, isMaxItemCountValid] = useFormItem<number>({
    type: InputType.TYPE_NUMBER,
    label: t(`projectForm.maxReports`),
    value: prefill?.maxItemCount || DEFAULT_PROJECT_MAX_ITEM_COUNT,
    validator: maxItemCountValidator,
    inputOptions: {
      disabled: disableInputs,
      showRequiredIndicator: true,
    },
  });

  const canSubmit = name && (!showProfileInput || isProfileValid) && isNameValid && isDescriptionValid && isMaxItemCountValid && !loading;

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      setError(null);
      await submit.action(name, description, profileId!, maxItemCount);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  const profilesLoaded = !showProfileInput || !!profiles;

  return (
    <Stack spacing={1.5} component={'form'} onSubmit={onSubmit}>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !profilesLoaded && <InlineLoader />}
      {profilesLoaded && (
        <>
          {nameComponent}
          {descriptionComponent}
          {showProfileInput && profileComponent}
          {maxItemCountComponent}
        </>
      )}
      {profilesLoaded && (
        <ButtonBox>
          <TertiaryButton disabled={loading} onClick={back?.action}>
            {back?.label}
          </TertiaryButton>
          <PrimaryButton disabled={!canSubmit} type="submit">
            {submit?.label}
          </PrimaryButton>
        </ButtonBox>
      )}
    </Stack>
  );
};
